import { BaseWidget } from "./BaseWidget";
import { SelectControl } from "../controls/SelectControl";
import { ColorControl } from "../controls/ColorControl";
import { BooleanControl } from "../controls/BooleanControl";
import { IconButtonGroupControl } from "../controls/IconButtonGroupControl";
import { PeriodOverrideControl } from "../controls/PeriodOverrideControl";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import useWidgetPersona from "./_shared/useWidgetPersona";
import QuizIcon from '@mui/icons-material/Quiz';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import { client } from "../../../../hooks/Client";
import { useFirestoreGeneral } from "../../../../firebase/useFirestore";
import { useContext } from "react";
import { fetchTemplateData } from "../utils/fetchTemplateData";
import { TemplateDataContext } from "../context/TemplateDataContext";
import { EditorContext } from "../context/EditorContextValue";
import IndicatorVisualization from "../../../dataVisualizations/IndicatorVisualization";
import WidgetSourcePlaceholder from "../components/WidgetSourcePlaceholder";
import notDeleted from "../../../../helpers/notDeleted";
import buildQuestionChoices from "../utils/buildQuestionChoices";
import useQuestionHeading from "../../../../hooks/useQuestionHeading";
import { definitionCollection } from '../../../../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// QuestionWidget — single-indicator (QuestionnaireField) chart. Renders via
// the shared `IndicatorVisualization` so it matches the dashboard's
// Indicator/Vraag card. No nested-item controls (single-item widget).

/**
 * @extends {BaseWidget}
 */
export class QuestionWidget extends BaseWidget {

    getName() { return 'Vraag'; }
    getCategory() { return 'research'; }
    getIcon() { return <QuizIcon />; }

    getControls() {
        return {
            questionId: new SelectControl({
                label: 'Vraag',
                description: 'Kies een vraag',
                defaultValue: '',
                options: async () => {
                    const isTemplate = new URLSearchParams(window.location.search).get('template') === 'true';
                    if (isTemplate) {
                        let values = [];
                        const json = await fetchTemplateData();
                        values = json?.data?.effects?.indicators || [];
                        return {
                            choices: values.map(o => ({ value: o.ID, label: o.Question })),
                            allowSearch: true,
                        };
                    }

                    /* Geen orderBy('Position') in de query: die laat velden zonder
                       Position stil weg. buildQuestionChoices sorteert zelf.
                       AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field */
                    const [fieldsSnap, questionnairesSnap] = await Promise.all([
                        definitionCollection('QuestionnaireFields').where('CompagnyID', '==', client || '').get(),
                        definitionCollection('Questionnaires').where('CompagnyID', '==', client || '').get(),
                    ]);

                    return {
                        choices: buildQuestionChoices(
                            fieldsSnap.docs.map(doc => doc.data()).filter(notDeleted),
                            // Ongefilterd: vragen van een vragenlijst in de prullenbak krijgen zo hun titel mee.
                            questionnairesSnap.docs.map(doc => doc.data()),
                        ),
                        allowSearch: true,
                    };
                },
            }),
            // Standaard uit: bestaande rapportages zetten vaak een eigen kop boven
            // de grafiek, en die mogen niet ineens een tweede krijgen.
            showQuestion: new BooleanControl({
                label: 'Vraag tonen',
                description: 'Zet de vraagtekst boven de grafiek, zodat lezers zien over welke vraag het gaat.',
                defaultValue: false,
            }),
            period: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de data van deze widget toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            graphType: new IconButtonGroupControl({
                label: 'Grafiektype',
                defaultValue: '',
                options: {
                    buttons: [
                        { value: 'bar', icon: <BarChartIcon />, tooltip: 'Staafdiagram' },
                        { value: 'line', icon: <ShowChartIcon />, tooltip: 'Lijndiagram' },
                        { value: 'total-growth', icon: <TrendingUpIcon />, tooltip: 'Totale groei' },
                        // Bestaat een type niet bij de gekozen vraag, dan valt de grafiek
                        // terug op de standaard (resolveWidgetGraphType).
                        { value: 'pie', icon: <PieChartIcon />, tooltip: 'Cirkeldiagram' },
                        { value: 'distribution', icon: <StackedBarChartIcon />, tooltip: 'Verdeling per antwoord (matrixvraag)' },
                        { value: 'distribution-pie', icon: <DonutSmallIcon />, tooltip: 'Cirkel per stelling (matrixvraag)' },
                        { value: 'metric', icon: <LooksOneIcon />, tooltip: 'Getal: het gemiddelde (schaalvraag)' },
                    ],
                },
            }),
            // Een keuzevraag legt niet vast of zijn antwoorden een schaal zijn;
            // standaard krijgen ze losse kleuren. 'Zeer goed' werd daardoor
            // koraal en las als negatief (testronde 2026-10-05).
            scaleColors: new SelectControl({
                label: 'Kleuren',
                description: 'Voor een keuzevraag met een schaal, zoals van zeer goed tot zeer slecht.',
                defaultValue: '',
                options: {
                    choices: [
                        { value: '', label: 'Standaard' },
                        { value: 'positive-first', label: 'Schaal: eerste antwoord is positief' },
                        { value: 'negative-first', label: 'Schaal: eerste antwoord is negatief' },
                    ],
                },
            }),
            graphColor: new ColorControl({
                label: 'Label- en legendakleur',
                description: 'Kleur van de labels en de legenda.',
                defaultValue: '',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} widget={this} />;
    }
}

/**
 * @param {object} props
 * @param {string} props.questionId
 * @param {string} [props.graphType]
 * @param {string} [props.graphColor]
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Widget period setting (PeriodOverrideControl)
 * @param {boolean} [props.showQuestion] - Toon de vraagtekst als kop boven de grafiek
 * @param {'positive-first'|'negative-first'|''} [props.scaleColors] - Kleur de antwoorden van een keuzevraag als schaal
 * @param {QuestionWidget} props.widget
 */
const Renderer = ({ questionId, graphType, graphColor, period, showQuestion, scaleColors }) => {
    const { templateData, isTemplate } = useContext(TemplateDataContext);
    const inEditor = Boolean(useContext(EditorContext));
    const personaId = useWidgetPersona();
    const { range: periodRange, startDate, endDate } = useWidgetPeriod(period);

    /* Existence check, separate from the chart's own data fetch: the hook
       returns '' while resolving and an array once Firestore answered. An
       empty array means the saved questionId doesn't exist in this tenant —
       the orphan state every data widget lands in after a template copy. */
    const liveQuestions = useFirestoreGeneral('QuestionnaireFields', 'ID', isTemplate ? '' : (questionId || ''));
    const questionMissing = !questionId
        || (!isTemplate && Array.isArray(liveQuestions) && liveQuestions.length === 0);

    /* Testronde 2026-10-02 (B6): op het canvas stond nergens welke vraag de
       grafiek toont. In een live rapportage verbergt IndicatorData zijn eigen
       kop met de vraag; de maker ziet hem hier altijd, de lezer alleen als
       'Vraag tonen' aan staat. */
    // In template-modus komt de vraag uit de templategegevens, net als in
    // IndicatorVisualization; de Firestore-opzoeking wordt daar overgeslagen.
    const question = isTemplate
        ? (templateData?.data?.effects?.indicators || []).find(i => i.ID === questionId)
        : (Array.isArray(liveQuestions) ? liveQuestions[0] : null);
    // Bij een meetstandaardvraag de naam van het effect, net als bij 'Onderzoek'.
    // Vóór de vroege return: een hook mag niet na een return staan.
    const questionText = useQuestionHeading(question);

    if (questionMissing) {
        return <WidgetSourcePlaceholder ctaLabel="Selecteer vraag" />;
    }

    return (
        <>
            {questionText && showQuestion && <h3 className="question-widget-title">{questionText}</h3>}
            {questionText && !showQuestion && inEditor && (
                <p className="question-widget-editor-caption">{questionText}</p>
            )}
            <IndicatorVisualization
                indicatorId={questionId}
                graphType={graphType}
                graphColor={graphColor}
                scaleColors={scaleColors}
                selectedPersonaId={personaId}
                startDate={startDate}
                endDate={endDate}
                periodRange={periodRange}
                templateData={isTemplate ? templateData : null}
            />
        </>
    );
};
